import React from 'react';
import {
    buildAssetSetDisplayDefinitions,
    getPanoCompatibleAssetSets,
    GOLDEN_DISPLAY_COLOR,
    normalizeDisplayID,
} from '../../helpers/assetSetVisualizationModel';
import PanoSceneManager from './PanoSceneManager';
import PanoEvents from '../Enumerations/ViewerEnums';
import FacilityMiniMap from '../../components/FacilityMiniMap';
import FacilityStore from '../../stores/FacilityStore';
import ViewerStore from '../../stores/ViewerStore';
import * as Inovx from '@shared';
import ViewerActions from '../../actions/ViewerActions';
import PanoViewer from './PanoViewer';
import FacilityActions from '../../actions/FacilityActions';
import AnnotationTextEditor from '../components/AnnotationTextEditor';
import AnnotationLinkEditor from '../components/AnnotationLinkEditor';
import { SpeedDialAnimationSettingsModel, SpeedDialComponent, SpeedDialItemEventArgs, SpeedDialItemModel, FabComponent } from '@syncfusion/ej2-react-buttons';
import AnnotationEditor from '../components/AnnotationEditor';
import ContextMenu from '../components/ContextMenu';
import KnowledgeBookActions from '../../actions/KnowledgeBookActions';
import AssetSetsActions from '../../actions/AssetSetsActions';
import KnowledgeBookStore from '../../stores/KnowledgeBookStore';
import AssetSetVisualizationStore from '../../stores/AssetSetVisualizationStore';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import ViewerHelpers from '../../helpers/ViewerHelpers';
import { mergeViewerAssetSets } from '../../helpers/viewerAssetSetModel';
import ContentLayersButton from './ContentLayersButton';
import { ContentLayers } from '../../Interfaces/ViewerInterfaces';
import { isBlackAndWhiteScanPosition, isScanPositionView } from '../../helpers/viewMedia';
import { hasPanoCameraChanged } from '../../helpers/knowledgeViewCameraState';

const { useState, useEffect, useRef, useMemo } = React;

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var SystemActions = Inovx.Shared.System.Actions;

type VolumeChangeMode =
    | 'tagmove'
    | 'tagscale'
    | 'assemblymove'
    | 'assemblyscale';
interface PanoViewerProps {
    displayMiniMap: boolean;
}

interface IPanoViewInfo {
    view: IView360Detail;
    kvID?: string;
    kvTags?: IViewAnnotationTag[];
    kvVersion?: number;
    cameraProperties?: IPanoCameraProperties;
    cameraDirection?: IVector3D;
}

interface IKVTagsInfo {
    tags: IViewAnnotationTag[];
    version: number;
}

const getPanoViewInfo = (): IPanoViewInfo => {
    const kv = ViewerStore.getSelectedKnowledgeView();
    return {
        view: ViewerStore.getSelectedViewImage(),
        kvID: kv?.ID,
        kvTags: kv?.Tags,
        kvVersion: ViewerStore.getKVContentVersion(),
        cameraProperties: (kv) ? { alpha: kv.DisplayProperties?.Yaw, beta: kv.DisplayProperties?.Pitch, fov: kv.DisplayProperties?.Fov } : null,
        cameraDirection: ViewerStore.getFocusedAnnotation()?.Target3D
    }
}

const getKVTagsInfo = (): IKVTagsInfo => {
    return {
        tags: ViewerStore.getSelectedKnowledgeView()?.Tags,
        version: ViewerStore.getTagsVersion()
    }
}

const getViewRenderSignature = (view?: IView360Detail | null): string => {
    if (!view) {
        return '';
    }

    const position = view.Position;
    const rotation = view.Rotation;

    return [
        view.ID,
        position?.X,
        position?.Y,
        position?.Z,
        rotation?.X,
        rotation?.Y,
        rotation?.Z,
        rotation?.W,
        view.ImageUrl,
        view.DepthMapUrl,
        view.Width,
        view.Height,
        view.Radius,
        view.ScaleFactor,
        view.PanoColorType
    ].join('|');
}

const ReactPanoViewer = React.forwardRef((props: PanoViewerProps, ref) => {
    const getKVAssetSetsSignature = (): string => {
        const kv = ViewerStore.getSelectedKnowledgeView();
        const sets = kv?.AssetSets ?? [];

        return sets
            .map(s => `${s.ID}:${s.Ord}:${s.Color}:${s.Hidden ? 1 : 0}`)
            .sort()
            .join('|');
    }

    const panoSceneManagerRef = useRef<PanoSceneManager>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Facility store data
    const [facilityTitle] = useState<string>(FacilityStore.getSelectedFacility().Name);
    const [siteMap, setSiteMap] = useState<ISiteMap>(getSiteMap());
    const [views, setViews] = useState<IView360Detail[]>(FacilityStore.getSelectedFacility().Views);
    const [assetTags, setAssetTags] = useState<IAssetTag[]>(FacilityStore.getSelectedFacility().AssetTags);
    const assetTagsRef = useRef<IAssetTag[]>(assetTags);

    // Viewer store data
    const [openPointCloud, isOpenPointCloud] = useState<boolean>(ViewerStore.isOpenPointCloud());
    const [measurements, setMeasurements] = useState<Measurement[]>(ViewerStore.getMeasurements());
    const [displayCompass, setDisplayCompass] = useState<boolean>(ViewerStore.getSettings().PanoCompassVisible);
    const [panoScanSphereColor, setPanoScanSphereColor] = useState<string | undefined>(ViewerStore.getSettings().PanoScanSphereColor);
    const [panoImageSphereColor, setPanoImageSphereColor] = useState<string | undefined>(ViewerStore.getSettings().PanoImageSphereColor);
    const [tagVolumeBoxColor, setTagVolumeBoxColor] = useState<string | undefined>(ViewerStore.getSettings().TagVolumeBoxColor);
    const [measurementColor, setMeasurementColor] = useState<string>(ViewerStore.getSettings().MeasurementColor);
    const [selectedView, setSelectedView] = useState<IPanoViewInfo>(getPanoViewInfo());
    const [selectedAnnotation, setSelectedAnnotation] = useState<string>(ViewerStore.getSelectedAnnotation());
    const [displayObstructedTags, setDisplayObstructedTags] = useState<boolean>(ViewerStore.getDisplayObstructedTags());
    const [focusedAnnotation, setFocusedAnnotation] = useState<IAnnotationDisplayItem>(ViewerStore.getFocusedAnnotation());
    const [focusedMeasurement, setFocusedMeasurement] = useState<Measurement>(ViewerStore.getFocusedMeasurement());
    const [occludedAnnotations, setOccludedAnnotations] = useState<string[]>(ViewerStore.getOccludedAnnotations());
    const [filteredAssets, setFilteredAssets] = useState<string[]>(ViewerStore.getFilteredAssets());
    const [visibleAssetTagClasses, setVisibleAssetTagClasses] = useState<string[] | null>(ViewerStore.getVisibleAssetTagClasses());
    const [contentLayers, setContentLayers] = useState<ContentLayers>(ViewerStore.getContentLayers());
    const [depthMapLoaded, setDepthMapLoaded] = useState<boolean>(ViewerStore.isDepthMapLoaded());
    const [displayMode, setDisplayMode] = useState<DisplayMode>(ViewerStore.getDisplayMode());
    const [measurementType, setMeasurementType] = useState<MeasurementType>(ViewerStore.getMeasurementType());
    const [kvTagsInfo, setKvTagsInfo] = useState<IKVTagsInfo>(getKVTagsInfo());
    const [kvName, setKvName] = useState<string>(ViewerStore.getSelectedKnowledgeView()?.Name);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isAnnotationTextShow, setIsAnnotationTextShow] = useState(false);
    const [isAnnotationDetailShow, setIsAnnotationDetailShow] = useState(false);
    const [isAnnotationLinkDetailShow, setIsAnnotationLinkDetailShow] = useState(false);
    const [lastAnnotationPosition, setLastAnnotationPosition] = useState({ x: 0, y: 0 });
    const [lastClickPosition, setLastClickPosition] = useState({ x: 0, y: 0 });

    const [knowledgeBookAssetSets, setKnowledgeBookAssetSets] = useState<IAssetSet[]>(KnowledgeBookStore.getAllAssetSet());
    const [facilityAssetSets, setFacilityAssetSets] = useState<IAssetSet[]>([]);
    const [visualizationConfiguration, setVisualizationConfiguration] =
        useState<IAssetSetDisplayConfiguration | null>(AssetSetVisualizationStore.getConfiguration());
    const [facilityAssetSetsBarOpen, setFacilityAssetSetsBarOpen] =
        useState<boolean>(FacilityStore.getFacilityAssetSetsBarOpen());
    const [assetSetVisualizationBarOpen, setAssetSetVisualizationBarOpen] =
        useState<boolean>(FacilityStore.getAssetSetVisualizationBarOpen());
    const [selectedFacilityID, setSelectedFacilityID] =
        useState<string | null>(FacilityStore.getSelectedFacility()?.ID ?? null);
    const assetSets = useMemo(
        () => mergeViewerAssetSets(knowledgeBookAssetSets, facilityAssetSets),
        [knowledgeBookAssetSets, facilityAssetSets]
    );
    const panoFacilityAssetSets = useMemo(
        () => getPanoCompatibleAssetSets(facilityAssetSets),
        [facilityAssetSets]
    );
    const displayAssetSetDefinitions = useMemo(
        () => buildAssetSetDisplayDefinitions(
            visualizationConfiguration,
            new Map(panoFacilityAssetSets.map(assetSet => [normalizeDisplayID(assetSet.ID), assetSet]))
        ),
        [panoFacilityAssetSets, visualizationConfiguration]
    );
    const displayAssetTagResolution = useMemo(
        () => ViewerHelpers.resolveAssetSetDisplayTags(displayAssetSetDefinitions),
        [displayAssetSetDefinitions]
    );

    const [kvAssetSetsSignature, setKvAssetSetsSignature] = useState<string>(getKVAssetSetsSignature());

    const [directionDegrees, setDirectionDegrees] = useState(0);
    const [activeView, setActiveView] = useState<IView360Detail>(null);
    const [highlightedView, setHighlightedView] = useState<IView360Detail>(null);

    const isSceneLoading = useRef<boolean>(true);
    const isSceneDirty = useRef<boolean>(false);
    const isSceneEditable = useRef<boolean>(false);
    const updateColorizedAssetSetsRef = useRef<() => void>(() => undefined);
    const updateDisplayedAnnotationsRef = useRef<() => void>(() => undefined);

    //ref for annotation popup
    const isAnnotationTextShowRef = useRef(isAnnotationTextShow);
    const isAnnotationDetailShowRef = useRef(isAnnotationDetailShow);
    const isAnnotationLinkDetailShowRef = useRef(isAnnotationLinkDetailShow);
    const [contextMenu, setContextMenu] = useState<boolean>(false);
    const [assetTagContextMenu, setAssetTagContextMenu] = useState<{ visible: boolean; left: number; top: number; tagId: string | null }>({ visible: false, left: -1000, top: -1000, tagId: null });

    const [forceRender, setForceRender] = useState(0);
    const [openedKnowledgeBook, setOpenedKnowledgeBook] = useState<IKnowledgeBook>(
        KnowledgeBookStore.getSelectedKnowledgeBook()
    );

    const hasManageContentPermission = useMemo(() => { return ComponentHelpers.hasManageContentPermission(); }, []);
    //modify and manage tags permissions
    const hasModifyTagsPermission = useMemo(() => { return ComponentHelpers.hasModifyTagsPermission(); }, []);
    const hasManageTagsPermission = useMemo(() => { return ComponentHelpers.hasManageTagsPermission(); }, []);

    const hasModifyKBPermission = React.useMemo(() => ComponentHelpers.hasModifyKnowledgeBookPermission(openedKnowledgeBook), [openedKnowledgeBook]);
    const hasManageKBPermission = React.useMemo(() => ComponentHelpers.hasManageKnowledgeBookPermission(openedKnowledgeBook), [openedKnowledgeBook]);

    const [knowledgeBookViewingMode, setKnowledgeBookViewingMode] = useState<KnowledgeBookViewingMode>(
        KnowledgeBookStore.getKnowledgeBookViewingMode()
    );

    const [selectedAssetSet, setSelectedAssetSet] = useState<IAssetSet | null>(
        ViewerStore.getSelectedAssetSet()
    );
    const activeFacilityMode: 'manage' | 'display' | null = facilityAssetSetsBarOpen
        ? 'manage'
        : assetSetVisualizationBarOpen
            ? 'display'
            : null;
    const isAssetSetManageMode = knowledgeBookViewingMode === 'manageAssets' || activeFacilityMode === 'manage';

    const forceUpdate = () => {
        setForceRender(prev => prev + 1); // Increment state to trigger re-render
    };

    const isLoading = () => {
        return isSceneLoading.current;
    }

    const isDirty = () => {
        return isSceneDirty.current;
    }

    const isEditable = () => {
        return isSceneEditable.current;
    }

    const getPanoScreenshot = async (width: number, height: number): Promise<string | null> => {
        const viewer = panoSceneManagerRef.current?.viewer;
        if (!viewer) {
            return null;
        }

        return new Promise<string | null>((resolve) => {
            viewer.getScreenshot(width, height, (image) => resolve(image || null));
        });
    }

    function getSiteMap() {
        const ontology = FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart();
        return ontology?.Map;
    }

    const updateFacilityState = () => {
        const facility = FacilityStore.getSelectedFacility();

        if (facility) {
            setSelectedFacilityID(facility.ID);
            setFacilityAssetSetsBarOpen(FacilityStore.getFacilityAssetSetsBarOpen());
            setAssetSetVisualizationBarOpen(FacilityStore.getAssetSetVisualizationBarOpen());
            if (ViewerStore.isPanoViewerVisible()) {
                setSiteMap(getSiteMap());
                setViews(facility.Views);
                setAssetTags(facility.AssetTags);
            }
        }
    };

    useEffect(() => {
        if (!selectedFacilityID) {
            setFacilityAssetSets([]);
            return;
        }

        let cancelled = false;
        const loadFacilityAssetSets = async () => {
            try {
                const loadedAssetSets = await AssetSetsActions.refreshFacilityAssetSets(selectedFacilityID, false);
                if (cancelled) return;
                setFacilityAssetSets((loadedAssetSets ?? []).filter(assetSet => !assetSet.KnowledgeBookID));
            } catch {
                // Preserve the current pano and the last resolved definitions if refresh fails.
            }
        };
        const handleAssetSetsChange = (facilityID: string) => {
            if (facilityID === selectedFacilityID) void loadFacilityAssetSets();
        };

        void loadFacilityAssetSets();
        AssetSetVisualizationStore.addAssetSetsChangeListener(handleAssetSetsChange);
        return () => {
            cancelled = true;
            AssetSetVisualizationStore.removeAssetSetsChangeListener(handleAssetSetsChange);
        };
    }, [selectedFacilityID]);
    const calculateMeasurementDistance = (m: Measurement) => {

    };

    const getDisplayedAssetTagsForPano = React.useCallback((): IAssetTag[] => {
        let displayedAssetTags = assetTags.filter(tag => !filteredAssets.includes(tag.AssetID));
        displayedAssetTags = ViewerHelpers.filterAssetTagsByVisibleClasses(displayedAssetTags, visibleAssetTagClasses, FacilityStore.getSelectedFacility());

        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (selectedKnowledgeView && !isAssetSetManageMode) {
            const resolution = ViewerHelpers.resolveKnowledgeViewAssetTags(
                selectedKnowledgeView.AssetSets,
                assetSets,
                displayedAssetTags.map(tag => tag.ID)
            );
            const visibleKnowledgeViewTagIDs = new Set(resolution.visibleTagIDs);
            const claimedTagIDs = new Set([...resolution.visibleTagIDs, ...resolution.hiddenTagIDs]);
            displayedAssetTags = displayedAssetTags.filter(tag =>
                visibleKnowledgeViewTagIDs.has(tag.ID) || (resolution.fallbackVisible && !claimedTagIDs.has(tag.ID)));
        } else if (activeFacilityMode === 'display') {
            const hiddenTagIDs = new Set(displayAssetTagResolution.hiddenTagIDs);
            const claimedTagIDs = new Set(Object.values(displayAssetTagResolution.tagIDsByAssetSetID).flat());
            displayedAssetTags = displayedAssetTags.filter(tag =>
                !hiddenTagIDs.has(tag.ID) &&
                (!displayAssetTagResolution.fallbackHidden || claimedTagIDs.has(tag.ID)));
        }

        return displayedAssetTags;
    }, [activeFacilityMode, assetSets, assetTags, displayAssetTagResolution, filteredAssets,
        isAssetSetManageMode, kvAssetSetsSignature, visibleAssetTagClasses]);

    const updateViewerState = () => {
        if (ViewerStore.isPanoViewerVisible()) {

            setSelectedView(prevState => {
                const newState = getPanoViewInfo();
                const viewChanged = getViewRenderSignature(newState.view) != getViewRenderSignature(prevState.view);
                return (viewChanged || newState.kvID != prevState.kvID || newState.kvVersion != prevState.kvVersion) ? newState : prevState;
            });

            setKvTagsInfo(prevState => {
                const newState = getKVTagsInfo();
                return (newState.version != prevState.version) ? newState : prevState;
            });

            setKvName(prevName => {
                const newName = ViewerStore.getSelectedKnowledgeView()?.Name;
                return (newName != prevName) ? newName : prevName;
            });

            setKvAssetSetsSignature(prevSig => {
                const newSig = getKVAssetSetsSignature();
                return newSig !== prevSig ? newSig : prevSig;
            });

            setMeasurements(ViewerStore.getMeasurements());
            setSelectedAnnotation(ViewerStore.getSelectedAnnotation());
            setDisplayObstructedTags(ViewerStore.getDisplayObstructedTags());
            setFocusedAnnotation(ViewerStore.getFocusedAnnotation());
            setFocusedMeasurement(ViewerStore.getFocusedMeasurement());
            setFilteredAssets(ViewerStore.getFilteredAssets());
            setVisibleAssetTagClasses(ViewerStore.getVisibleAssetTagClasses());
            setContentLayers(ViewerStore.getContentLayers());
            setOccludedAnnotations(ViewerStore.getOccludedAnnotations());
            setDisplayMode(ViewerStore.getDisplayMode());
            setMeasurementType(ViewerStore.getMeasurementType());
            const settings = ViewerStore.getSettings();
            setDisplayCompass(settings.PanoCompassVisible);
            setPanoScanSphereColor(settings.PanoScanSphereColor);
            setPanoImageSphereColor(settings.PanoImageSphereColor);
            setTagVolumeBoxColor(settings.TagVolumeBoxColor);
            setMeasurementColor(settings.MeasurementColor);
            setDepthMapLoaded(ViewerStore.isDepthMapLoaded());
            isOpenPointCloud(ViewerStore.isOpenPointCloud());
        }
    };

    const isInKnowledgeViewMode = () => {
        return (selectedView?.kvID) ? true : false;
    }

    const updateAppTitle = () => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement) {
            let title: string = isInKnowledgeViewMode() ? kvName : selectedView.view?.Name ?? "";
            if (isEditable()) {
                title += ` ${InovxLoader.t('facility:viewer.editMode')}`
            }

            headerBarTitleElement.textContent = title;
            headerBarTitleElement.setAttribute("title", title);
        }
    }

    const updateColorizedAssetSetsInPanoViewer = React.useCallback(() => {
        const sceneManager = panoSceneManagerRef.current;
        if (!sceneManager?.viewer) {
            return;
        }

        sceneManager.resetAllAnnotationColorization();

        if (isAssetSetManageMode) {
            if (!selectedAssetSet) {
                return;
            }

            sceneManager.colorizeAnnotations(
                selectedAssetSet.AssetTagIDs || [],
                GOLDEN_DISPLAY_COLOR
            );
            return;
        }

        const kv = ViewerStore.getSelectedKnowledgeView();
        if (kv) {
            const kvSets = kv.AssetSets ?? [];
            const tagResolution = ViewerHelpers.resolveKnowledgeViewAssetTags(
                kvSets,
                assetSets,
                assetTags.map(tag => tag.ID)
            );

            kvSets.forEach(set => {
                const tagIDs = tagResolution.tagIDsByAssetSetID[set.ID] ?? [];
                if (!set.Hidden && tagIDs.length) {
                    sceneManager.colorizeAnnotations(tagIDs, set.Color);
                }
            });
            const allAssetTags = assetSets.find(assetSet => assetSet.Type === AssetSetType.AllAssetTags);
            const fallbackEntry = allAssetTags
                ? kvSets.find(set => set.ID.toLocaleLowerCase() === allAssetTags.ID.toLocaleLowerCase())
                : undefined;
            if (fallbackEntry && !fallbackEntry.Hidden && fallbackEntry.Color != null) {
                const claimedTagIDs = new Set(Object.values(tagResolution.tagIDsByAssetSetID).flat());
                const fallbackTagIDs = getDisplayedAssetTagsForPano()
                    .map(tag => tag.ID)
                    .filter(tagID => !claimedTagIDs.has(tagID));
                sceneManager.colorizeAnnotations(fallbackTagIDs, fallbackEntry.Color);
            }
            return;
        }

        if (activeFacilityMode === 'display') {
            displayAssetSetDefinitions.forEach(definition => {
                if (definition.isAllAssetTags || definition.mode !== 'color') return;
                const tagIDs = displayAssetTagResolution.tagIDsByAssetSetID[definition.id] ?? [];
                if (tagIDs.length) {
                    sceneManager.colorizeAnnotations(tagIDs, definition.colorKey);
                }
            });

            const fallback = displayAssetSetDefinitions.find(definition => definition.isAllAssetTags);
            if (fallback?.mode === 'color') {
                const claimedTagIDs = new Set(Object.values(displayAssetTagResolution.tagIDsByAssetSetID).flat());
                const fallbackTagIDs = getDisplayedAssetTagsForPano()
                    .map(tag => tag.ID)
                    .filter(tagID => !claimedTagIDs.has(tagID));
                if (fallbackTagIDs.length) {
                    sceneManager.colorizeAnnotations(fallbackTagIDs, fallback.colorKey);
                }
            }
        }
    }, [activeFacilityMode, assetSets, displayAssetSetDefinitions, displayAssetTagResolution,
        getDisplayedAssetTagsForPano, isAssetSetManageMode, selectedAssetSet]);
    updateColorizedAssetSetsRef.current = updateColorizedAssetSetsInPanoViewer;

    useEffect(() => {
        updateColorizedAssetSetsInPanoViewer();
    }, [updateColorizedAssetSetsInPanoViewer, kvAssetSetsSignature, assetSets]);

    const resetAppTitle = () => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement) {
            headerBarTitleElement.textContent = "";
            headerBarTitleElement.removeAttribute("title");
        }
    }

    const navigateToView = async (view: IView360Detail) => {
        // Prevent navigation if there are unsaved knowledge view changes
        if (ViewerActions.checkForKnowledgeViewChanges()) return;
        
        //if selected view and clicked view are different
        const oldViewImage = ViewerStore.getSelectedViewImage();
        if (!oldViewImage || view.ID !== oldViewImage.ID) {
            await ViewerActions.showPanoViewerImage(view);
        }
    }

    const updateViewerCamera = (viewer: PanoViewer) => {
        if (selectedView.cameraDirection) {
            panoSceneManagerRef.current?.alignToTarget(selectedView.cameraDirection);
        }
        else if (selectedView.cameraProperties && selectedView.cameraProperties.alpha && selectedView.cameraProperties.beta && selectedView.cameraProperties.fov) {
            viewer.setCameraProperties(selectedView.cameraProperties.alpha, selectedView.cameraProperties.beta, selectedView.cameraProperties.fov);
        }
    }

    const setCameraAsDirty = (camera: { yaw: number; pitch: number; fov: number }) => {
        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (FacilityStore.getKnowledgeViewEditBarOpen()
            && selectedKnowledgeView
            && hasPanoCameraChanged(selectedKnowledgeView.DisplayProperties, camera)) {
            ViewerActions.setCameraAsDirty();
            forceUpdate();
        }
    }
    const isCameraDirty = () => {
        return ViewerStore.isCameraDirty()
    }

    const isAnnotationWithDetail = (annotation: IViewAnnotationTag) => {
        return annotation.AnnotationType == AnnotationType.InformationAnnotation
            || annotation.AnnotationType == AnnotationType.LinkAnnotation || annotation.AnnotationType == AnnotationType.ObservationTag;
    }

    // Viewer initialization
    useEffect(() => {

        const handleFacilityStoreChange = () => {
            updateFacilityState();
        };


        const handleViewerStoreChange = () => {
            setSelectedAssetSet(ViewerStore.getSelectedAssetSet());
            updateViewerState();
        };

        const handleKnowledgeBookStoreChange = () => {
            setOpenedKnowledgeBook(KnowledgeBookStore.getSelectedKnowledgeBook());
            setKnowledgeBookViewingMode(KnowledgeBookStore.getKnowledgeBookViewingMode());
            setKnowledgeBookAssetSets(KnowledgeBookStore.getAllAssetSet());
        };

        const handleAssetSetVisualizationStoreChange = () => {
            setVisualizationConfiguration(AssetSetVisualizationStore.getConfiguration());
        };

        const handleFocus = (data: any) => {
            console.log(data);
        }

        const checkForUnsavedChanges = (event: any) => {
            if (isDirty()) {
                event.returnValue = InovxLoader.t("common:msgBox.browserSaveChanges");
            };
        }

        const viewClickedEventHandler = (e: CustomEvent) => {
            // The user clicked on a sphere in the pano viewer
            navigateToView(e.detail.view);
        }
        const viewHighlightedEventHandler = (e: CustomEvent) => {
            // The user hovered over a sphere in the pano viewer
            setHighlightedView(e.detail.view);
        }

        const labelSelectEventHandler = (e: CustomEvent) => {
            if (!ViewerStore.getAnnotationEditorMode()) {
                if (e.detail.annotationID) {
                    const selectedKVTag = ViewerStore.getSelectedKnowledgeView()?.Tags?.find(tag => tag.ID == e.detail.annotationID);
                    const selectedAssetTag = assetTagsRef.current?.find(tag => tag.ID == e.detail.annotationID);
                    if (selectedKVTag) {
                        setLastAnnotationPosition({ x: Math.round(e.detail.position.x), y: Math.round(e.detail.position.y) });
                        if (e.detail?.isRightClick) {
                            setLastClickPosition({ x: Math.round(e.detail.pickPosition.x), y: Math.round(e.detail.pickPosition.y) });
                            setContextMenu(true);
                            setAssetTagContextMenu({ visible: false, left: -1000, top: -1000, tagId: null });
                        } else {
                            setContextMenu(false);
                        }
                    } else if (selectedAssetTag && e.detail?.isRightClick) {
                        // Right-click on asset tag - show asset tag context menu
                        setAssetTagContextMenu({ 
                            visible: true, 
                            left: Math.round(e.detail.pickPosition.x), 
                            top: Math.round(e.detail.pickPosition.y), 
                            tagId: e.detail.annotationID 
                        });
                        setContextMenu(false);
                    } else {
                        setContextMenu(false);
                        setAssetTagContextMenu({ visible: false, left: -1000, top: -1000, tagId: null });
                    }
                }
                else {
                    setContextMenu(false);
                    setAssetTagContextMenu({ visible: false, left: -1000, top: -1000, tagId: null });
                    setLastAnnotationPosition({ x: 0, y: 0 });
                    setLastClickPosition({ x: 0, y: 0 });
                }
                ViewerActions.selectAnnotation(e.detail.annotationID);
            } else {
                setContextMenu(false);
                setAssetTagContextMenu({ visible: false, left: -1000, top: -1000, tagId: null });
            }
        }
        const modeMap: Record<string, VolumeChangeMode> = {
            tvlm: 'tagmove',
            tvls: 'tagscale',
        };
        const annUpdateEventHandler = async (e: CustomEvent) => { 
            let tag = (e.detail as any).annotation;
            const assetTag = FacilityStore.getSelectedFacility().AssetTags.find((item) => item.ID === tag.ID);
            if (assetTag) {
                tag = { ...tag, VersionToken: assetTag.VersionToken };
                await ViewerActions.saveUpdatedAssetTag(tag, false);
            } 
        }

        let degreesChangeTimeout: NodeJS.Timeout = null;

        const updateViewerProperties = (viewer: PanoViewer) => {
            if (!degreesChangeTimeout) {
                degreesChangeTimeout = setTimeout(() => {
                    
                    const camera = {
                        yaw: viewer.cameraAlpha,
                        pitch: viewer.cameraBeta,
                        fov: viewer.cameraFov,
                    };
                    ViewerActions.setCameraProperties(camera.yaw, camera.pitch, camera.fov);
                    setDirectionDegrees(viewer.cameraDirectionDegrees);
                    setCameraAsDirty(camera);
                    degreesChangeTimeout = null;
                    if (!ViewerStore.getAnnotationEditorMode()) {
                        if (isAnnotationDetailShowRef.current || isAnnotationLinkDetailShowRef.current) {
                            if (isAnnotationDetailShowRef.current) setIsAnnotationDetailShow(false)
                            if (isAnnotationLinkDetailShowRef.current) setIsAnnotationLinkDetailShow(false)
                            ViewerActions.resetAnnotation();
                        }
                        setContextMenu(false);
                    }
                }, 20); // 20ms delay to avoid too many updates
            }
        }

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        KnowledgeBookStore.addChangeListener(handleKnowledgeBookStoreChange);
        AssetSetVisualizationStore.addChangeListener(handleAssetSetVisualizationStoreChange);
        
        window.addEventListener('beforeunload', checkForUnsavedChanges);
        
        const scene = new PanoSceneManager(containerRef.current, selectedView.view, views, {
            showTripods: !isInKnowledgeViewMode() && contentLayers.positions360,
            showScanPositions: contentLayers.positions360,
            showColorScanPositions: contentLayers.colorScanPositions,
            showBlackAndWhiteScanPositions: contentLayers.blackAndWhiteScanPositions,
            showImagePositions: contentLayers.positions360 && contentLayers.imagePositions,
            scanSphereColor: panoScanSphereColor,
            imageSphereColor: panoImageSphereColor,
            tagVolumeColor: tagVolumeBoxColor,
            measurementColor: measurementColor
        });
        scene.events.addEventListener(PanoEvents.Viewer.VIEW_CLICKED, viewClickedEventHandler);
        scene.events.addEventListener(PanoEvents.Viewer.VIEW_HIGHLIGHTED, viewHighlightedEventHandler);
        scene.events.addEventListener(PanoEvents.Viewer.LABEL_SELECTION, labelSelectEventHandler);
        scene.events.addEventListener(PanoEvents.Viewer.ANNOTATION_UPDATE, annUpdateEventHandler);

        panoSceneManagerRef.current = scene;
        ViewerActions.setPanoScreenshotProvider(getPanoScreenshot);

        // Initialize the pano scene. Note that this will also create the pano viewer.
        // The init function also loads the depth map (which is optional for the functionality
        // of the viewer). Therefore, before the depth map is loaded, the onViewerReady() callback
        // is fired. This is where we can mark the viewer as initialized and show the minimap.
        const initialAnnotations = selectedView.kvTags ?? [];

        scene.init(initialAnnotations, ViewerStore.getCameraDirection() as any, viewer => {
            viewer.toggleCompass(displayCompass && hasValidRotation);

            viewer.events.addEventListener(PanoEvents.Viewer.VIEWER_DIRECTION_CHANGED, () => {
                updateKbDescriptionPopup(viewer)
                updateViewerProperties(viewer);
            });
            viewer.events.addEventListener(PanoEvents.Viewer.VIEWER_FOV_CHANGED, () => {
                updateViewerProperties(viewer);
            });

            //update viewer properties for first load
            updateViewerProperties(viewer);

            updateViewerCamera(viewer);
            isSceneLoading.current = false;
            isSceneDirty.current = false;

            updateColorizedAssetSetsRef.current();
            updateDisplayedAnnotationsRef.current();

            // Setting the active view will display the minimap
            setActiveView(selectedView.view);
        }, () => {
            ViewerActions.depthMapLoaded(scene.updateOccludedAnnotations(assetTags));
        });

        return () => {
            resetAppTitle(); // reset page title

            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);
            KnowledgeBookStore.removeChangeListener(handleKnowledgeBookStoreChange);
            AssetSetVisualizationStore.removeChangeListener(handleAssetSetVisualizationStoreChange);
            window.removeEventListener('beforeunload', checkForUnsavedChanges);
            panoSceneManagerRef.current?.events.removeEventListener(PanoEvents.Viewer.VIEW_CLICKED, viewClickedEventHandler);
            panoSceneManagerRef.current?.events.removeEventListener(PanoEvents.Viewer.VIEW_HIGHLIGHTED, viewHighlightedEventHandler);
            panoSceneManagerRef.current?.events.removeEventListener(PanoEvents.Viewer.LABEL_SELECTION, labelSelectEventHandler);
            panoSceneManagerRef.current?.events.removeEventListener(PanoEvents.Viewer.ANNOTATION_UPDATE, annUpdateEventHandler);
            ViewerActions.setPanoScreenshotProvider(null);

            panoSceneManagerRef.current?.dispose();
        };
    }, []);

    const hasValidRotation = useMemo(() => {
        const rotation = selectedView?.view?.Rotation;
        return (
            rotation?.X != null &&
            rotation?.Y != null &&
            rotation?.Z != null &&
            rotation?.W != null
        );
    }, [selectedView]);

    const updateKbDescriptionPopup = (viewer: PanoViewer) => {
        const { yaw, pitch, fov } = ViewerStore.getViewerCameraProperties();
        const { cameraAlpha, cameraBeta, cameraFov } = viewer;
        if (yaw !== 0 || pitch !== 0 || fov !== 0) {
            if (yaw !== cameraAlpha || pitch !== cameraBeta || fov !== cameraFov) {
                if (KnowledgeBookStore.getShowKnowledgeBookDescription()) KnowledgeBookActions.showKnowledgeBookDescription(false);
            }
        }
    }

    useEffect(() => {
        if (openPointCloud)
            ViewerActions.updateViewerDirection(1, panoSceneManagerRef.current?.getViewerDirection());
    }, [openPointCloud]);

    // Update panoSceneManager with new measurements whenever measurements state changes
    useEffect(() => {
        const fetchMeasurements = async () => {
            if (isInKnowledgeViewMode() || selectedView.view) {
                let measurements = isInKnowledgeViewMode() ? ViewerStore.getSelectedKnowledgeView().Measurements : await FacilityActions.getView360Measurements(selectedView.view.ID);

                for (var x = 0; x < measurements.length; x++) {
                    var measurement = measurements[x];
                    
                    var distance;

                    var startPosition = new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z);
                    var endPosition = measurement.Type == MeasurementType.Point ? null : new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z);

                    if (measurement.Type == MeasurementType.Distance) {

                        distance = (endPosition.subtract(startPosition).length());
                    }
                    else if (measurement.Type == MeasurementType.XOrtho) {

                        var pB = new BABYLON.Vector3(endPosition.x, startPosition.y, startPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }
                    else if (measurement.Type == MeasurementType.YOrtho) {

                        var pB = new BABYLON.Vector3(startPosition.x, endPosition.y, startPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }
                    else if (measurement.Type == MeasurementType.ZOrtho) {

                        var pB = new BABYLON.Vector3(startPosition.x, startPosition.y, endPosition.z);
                        var pA = new BABYLON.Vector3(startPosition.x, startPosition.y, startPosition.z);

                        distance = (pB.subtract(pA).length());
                    }

                    //var unitInfo = ViewerStore.getUnitInformation();
                    var jsonMeasurement = measurement.Type == MeasurementType.Point
                        ? { checked: false, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, id: measurement.ID, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z) }
                        : { checked: false, name: measurement.Name, versionToken: measurement.VersionToken, saved: true, measureMode: measurement.Type, startPosition: new BABYLON.Vector3((measurement as any).Coordinates[0].X, (measurement as any).Coordinates[0].Y, (measurement as any).Coordinates[0].Z), endPosition: new BABYLON.Vector3((measurement as any).Coordinates[1].X, (measurement as any).Coordinates[1].Y, (measurement as any).Coordinates[1].Z), distance: distance, id: measurement.ID };

                    ViewerActions.createMeasurement(jsonMeasurement as any);
                }
            }
        };

        if (depthMapLoaded) {
            fetchMeasurements();
        }
    }, [depthMapLoaded, selectedView]);

    // Update panoSceneManager with new measurements whenever measurements state changes
    useEffect(() => {
        if (panoSceneManagerRef.current?.isDepthMapLoaded)
            panoSceneManagerRef.current?.setMeasurements(measurements);
    }, [measurements]);

    // Update the pano scene when the active view or KV changes
    useEffect(() => {
        if (isLoading())
            return;

        if (selectedView.view != activeView) {
            isSceneLoading.current = true;
            isSceneDirty.current = false;

            // Initiate the view change sequence. This will do everything from loading
            // the new image to loading and updating the depth map.
            const showTripods = !isInKnowledgeViewMode() && contentLayers.positions360;
            const cameraProperties = isInKnowledgeViewMode() ? selectedView.cameraProperties : null;

            const initialAnnotations = selectedView.kvTags ?? [];
            panoSceneManagerRef.current?.changeSelectedView(selectedView.view, initialAnnotations, cameraProperties, showTripods, () => {
                updateViewerCamera(panoSceneManagerRef.current.viewer);
                isSceneLoading.current = false;
                isSceneDirty.current = false;

                panoSceneManagerRef.current.viewer.toggleCompass(displayCompass && hasValidRotation);

                updateColorizedAssetSetsRef.current();
                updateDisplayedAnnotationsRef.current();

                // The view change is complete. Depth map still needs to be loaded, but
                // at this point we can update the active view (and thus the minimap).
                setActiveView(selectedView.view);
            }, () => {
                const occludedAnnotations = panoSceneManagerRef.current?.updateOccludedAnnotations(assetTagsRef.current) ?? [];
                ViewerActions.depthMapLoaded(occludedAnnotations);
            });
        }
        else {
            isSceneLoading.current = true;

            const showTripods = !isInKnowledgeViewMode() && contentLayers.positions360;
            panoSceneManagerRef.current?.setTripodsDisplay(showTripods);
            updateViewerCamera(panoSceneManagerRef.current.viewer);

            isSceneLoading.current = false;
            isSceneDirty.current = false;
        }
    }, [selectedView, contentLayers.positions360, contentLayers.colorScanPositions, contentLayers.blackAndWhiteScanPositions, contentLayers.imagePositions]);

    useEffect(() => {
        if (isLoading() || !panoSceneManagerRef.current) {
            return;
        }

        panoSceneManagerRef.current.setTripodsDisplay(!isInKnowledgeViewMode() && contentLayers.positions360);
        panoSceneManagerRef.current.setScanPositionsDisplay(contentLayers.positions360);
        panoSceneManagerRef.current.setColorScanPositionsDisplay(contentLayers.colorScanPositions);
        panoSceneManagerRef.current.setBlackAndWhiteScanPositionsDisplay(contentLayers.blackAndWhiteScanPositions);
        panoSceneManagerRef.current.setImagePositionsDisplay(contentLayers.positions360 && contentLayers.imagePositions);
    }, [contentLayers.positions360, contentLayers.colorScanPositions, contentLayers.blackAndWhiteScanPositions, contentLayers.imagePositions, selectedView]);

    useEffect(() => {
        isSceneEditable.current = hasModifyKBPermission && isInKnowledgeViewMode() && isEditing;
        if (!isEditable() && isDirty()) {
            isSceneDirty.current = false;
            forceUpdate();
        }
        if (isEditing) ViewerActions.resetDisplayMode();
    }, [hasModifyKBPermission, isEditing]);

    // Re-align the camera to a selected annotation
    useEffect(() => {
        if (isLoading())
            return;

        if (focusedAnnotation?.Target3D) {
            panoSceneManagerRef.current?.alignToTarget(focusedAnnotation.Target3D);
        }
    }, [focusedAnnotation]);

    useEffect(() => {
        if (isLoading())
            return;

        if (focusedMeasurement != null) {

            var point: IVector3D;
            var centerPoint: any;
            if (focusedMeasurement.measureMode == MeasurementType.Point) {
                point = { X: (focusedMeasurement as any).startPosition.x, Y: (focusedMeasurement as any).startPosition.y, Z: (focusedMeasurement as any).startPosition.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.Distance) {

                centerPoint = (focusedMeasurement as any).startPosition.add((focusedMeasurement as any).endPosition.subtract((focusedMeasurement as any).startPosition).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.XOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).endPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.YOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).endPosition.y, (focusedMeasurement as any).startPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }
            else if (focusedMeasurement.measureMode == MeasurementType.ZOrtho) {

                var pB = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).endPosition.z);
                var pA = new BABYLON.Vector3((focusedMeasurement as any).startPosition.x, (focusedMeasurement as any).startPosition.y, (focusedMeasurement as any).startPosition.z);

                centerPoint = pA.add(pB.subtract(pA).scale(.5));
                point = { X: centerPoint.x, Y: centerPoint.y, Z: centerPoint.z };
            }

            panoSceneManagerRef.current?.alignToTarget(point);
        }
    }, [focusedMeasurement]);

    const updateDisplayedAnnotationsInPanoViewer = React.useCallback(() => {
        if (isLoading() || !panoSceneManagerRef.current) {
            return;
        }

        let allDisplayedTags: IAnnotationDisplayItem[] = kvTagsInfo.tags ? [...kvTagsInfo.tags] : [];

        const depthMapLoaded = panoSceneManagerRef.current?.isDepthMapLoaded;
        if (depthMapLoaded) {
            if (contentLayers.assetTags) {
                const displayedAssetTags = getDisplayedAssetTagsForPano();
                allDisplayedTags = allDisplayedTags.concat(displayedAssetTags);
            }
        }

        if (!displayObstructedTags) {
            allDisplayedTags = allDisplayedTags.filter(tag =>
                !occludedAnnotations.some(otID => otID === tag.ID)
            );
        }

        const annotationObj = allDisplayedTags.find(tag => tag.ID === selectedAnnotation) || null;

        panoSceneManagerRef.current.setAnnotations(
            allDisplayedTags,
            annotationObj,
            !isEditable()
        );

        updateColorizedAssetSetsInPanoViewer();

    }, [
        kvTagsInfo,
        selectedAnnotation,
        occludedAnnotations,
        displayObstructedTags,
        contentLayers.assetTags,
        isEditing,
        getDisplayedAssetTagsForPano,
        updateColorizedAssetSetsInPanoViewer
    ]);
    updateDisplayedAnnotationsRef.current = updateDisplayedAnnotationsInPanoViewer;

    useEffect(() => {
        updateDisplayedAnnotationsInPanoViewer();
    }, [updateDisplayedAnnotationsInPanoViewer]);

    useEffect(() => {
        const id = setTimeout(() => {
            updateAppTitle();
        }, 300); // Delay to ensure state updates are settled

        return () => {
            clearTimeout(id);
        }
    }, [selectedView, kvName, isEditing]);

    useEffect(() => {
        isAnnotationLinkDetailShowRef.current = isAnnotationLinkDetailShow;
        isAnnotationDetailShowRef.current = isAnnotationDetailShow;
        isAnnotationTextShowRef.current = isAnnotationTextShow;
    }, [isAnnotationLinkDetailShow, isAnnotationDetailShow, isAnnotationTextShow ]);

    // Keep assetTagsRef in sync with assetTags state for use in event handlers
    useEffect(() => {
        assetTagsRef.current = assetTags;
    }, [assetTags]);

    useEffect(() => {
        if (selectedAnnotation) {
            const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
            if (selectedKVTag && isAnnotationWithDetail(selectedKVTag) && !contextMenu) {
                if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
                    //show the popup with the annotation detail (rich text control)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(true)
                    setIsAnnotationTextShow(false)
                } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
                    setIsAnnotationDetailShow(false);
                    setIsAnnotationLinkDetailShow(false)
                }
            }
            else {
                if (!ViewerStore.getAnnotationEditorMode()) {
                    //hide any existing annotation detail popup
                    setIsAnnotationDetailShow(false)
                    setIsAnnotationLinkDetailShow(false)
                    setIsAnnotationTextShow(false)
                }
            }
        } else {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            resetAnnotationEditorMode();
        }
    }, [selectedAnnotation]);

    const resetAnnotationEditorMode = () => {
        setTimeout(() => {
            ViewerActions.setAnnotationEditorMode(false)
        }, 1)
    }

    useEffect(() => {
        panoSceneManagerRef.current?.viewer?.setMeasurementType(measurementType);
    }, [measurementType]);

    useEffect(() => {
        const pointClickedEventHandler = async (e: CustomEvent) => {
            const point3D: IVector3D = e.detail.point3D;
            const point2D: IVector2D = e.detail.point2D;

            const cameraPosition: IVector3D = e.detail.cameraPosition;
            setLastAnnotationPosition({ x: point2D.X, y: point2D.Y });

            if (displayMode === DisplayMode.CreateAnnotation) {
                const annotationType = ViewerStore.getAnnotationType();
                if (annotationType === AnnotationType.AssetTag || annotationType === AnnotationType.AssetTagWithBox) {
                    await ViewerActions.createTempAssetTag(point3D, cameraPosition);
                } else {
                    setContextMenu(false)
                    const annotationID = await ViewerActions.createAnnotation(point3D);
                    ViewerActions.selectAnnotation(annotationID);

                    ViewerActions.setAnnotationEditorMode(true);
                    if (annotationType === AnnotationType.ObservationTag) {
                        setIsAnnotationTextShow(true);
                    }
                }
            }
            else {
                // Check if it's an asset tag
                const assetTag = assetTags.find(tag => tag.ID == e.detail.annotationID);
                if (assetTag) {
                    await ViewerActions.repositionAssetTag(assetTag, point3D, cameraPosition);
                } else {
                    // Check if it's a knowledge view annotation
                    const kvAnnotation = kvTagsInfo.tags?.find(t => t.ID == selectedAnnotation);
                    if (kvAnnotation) {
                        await ViewerActions.repositionAnnotation(kvAnnotation, point3D);
                        ViewerActions.resetAnnotation();
                    }
                }
            }

            ViewerActions.resetDisplayMode();
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                ViewerActions.resetDisplayMode();
            } else if (event.key === 'Enter' && (displayMode === DisplayMode.TagVolumeMove || displayMode === DisplayMode.TagVolumeScale)) {
                ViewerActions.resetDisplayMode();
            }
        };
        
        panoSceneManagerRef.current?.viewer?.setDisplayMode(displayMode);

        if (measurementType !== undefined || displayMode === DisplayMode.CreateAnnotation || displayMode === DisplayMode.PositionAnnotation || displayMode === DisplayMode.TagVolumeMove || displayMode === DisplayMode.TagVolumeScale) {
            panoSceneManagerRef.current?.events.addEventListener(PanoEvents.Viewer.POINT3D_CLICKED, pointClickedEventHandler);
            window.addEventListener('keydown', handleKeyDown);
        }
        if (displayMode === DisplayMode.Measurement) {
            if (isEditing) {
                setIsEditing(false);
                resetAnnotationEditorMode();
            }

            if (isAnnotationTextShow) {
                setIsAnnotationTextShow(false)
            }
        }

        return () => {
            if (measurementType !== undefined || displayMode === DisplayMode.CreateAnnotation || displayMode === DisplayMode.PositionAnnotation || displayMode === DisplayMode.TagVolumeMove || displayMode === DisplayMode.TagVolumeScale) {
                panoSceneManagerRef.current?.events.removeEventListener(PanoEvents.Viewer.POINT3D_CLICKED, pointClickedEventHandler);
                window.removeEventListener('keydown', handleKeyDown);
            }
        };

    }, [displayMode, measurementType]);

    useEffect(() => {
        panoSceneManagerRef.current?.viewer?.toggleCompass(displayCompass && hasValidRotation);
    }, [displayCompass, hasValidRotation]);

    useEffect(() => {
        panoSceneManagerRef.current?.changeScanSphereColor(panoScanSphereColor);
    }, [panoScanSphereColor]);

    useEffect(() => {
        panoSceneManagerRef.current?.changeImageSphereColor(panoImageSphereColor);
    }, [panoImageSphereColor]);

    useEffect(() => {
        panoSceneManagerRef.current?.changeTagVolumeColor(tagVolumeBoxColor);
    }, [tagVolumeBoxColor]);

    useEffect(() => {
        panoSceneManagerRef.current?.viewer?.changeMeasurementColor(measurementColor);
    }, [measurementColor]);

    const onMapViewHighlighted = (view: IView360Detail) => {
        // No higlighting if the scene is busy (i.e. changing views)
        if (!panoSceneManagerRef.current?.isBusy) {
            if (view) {
                panoSceneManagerRef.current?.highlightView(view.ID);
            }
            else {
                panoSceneManagerRef.current?.clearHighlightedView();
            }
        }
    }

    const onViewerSave = async () => {
        // This will reset the internal editing/dirty flags through the isEditing useEffect handler.
        await ViewerActions.saveKnowledgeViewChanges();
    }

    const onCameraSave = async () => {
        await onViewerSave();
        ViewerActions.resetCameraAsDirty()
    }

    const openAnnotation = () => {
        const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
        if (selectedKVTag && !ViewerStore.getAnnotationEditorMode()) {
            ViewerActions.setAnnotationEditorMode(true)
        }
        if (selectedKVTag.AnnotationType === AnnotationType.InformationAnnotation) {
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(false)
            setIsAnnotationDetailShow(true);
        } else if (selectedKVTag.AnnotationType === AnnotationType.LinkAnnotation) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationTextShow(false)
            setIsAnnotationLinkDetailShow(true)
        } else if (selectedKVTag.AnnotationType === AnnotationType.ObservationTag) {
            setIsAnnotationDetailShow(false);
            setIsAnnotationLinkDetailShow(false)
            setIsAnnotationTextShow(true)
        }
    }
    function annotationActionTemplate(props: any): JSX.Element {
        let classname: string = "s-icons " + props.properties.iconCss;
        return (
            <div className="s-icon-container">
                <span title={props.properties.title} className={classname}></span>
            </div>);
    }

    const handleAnnotationMenuAction = (type: 'edit' | 'delete' | 'reposition') => {
        setContextMenu(false);
        if (type === 'delete') {
            deleteAnnotation();
        } else if (type === 'edit') {
            openAnnotation();
        } else if (type === 'reposition') {
            // Make sure annotation detail popups are closed
            setIsAnnotationDetailShow(false);
            setIsAnnotationLinkDetailShow(false);
            setIsAnnotationTextShow(false);

            ViewerActions.setPositionAnnotationDisplayMode();
        }
    };

    const annotationContextItems = React.useMemo(() => {
        const items = [{ id: 'edit', label: InovxLoader.t('facility:action.editAnnotation'), iconClass: 'edit-icon' }];
        if(hasManageKBPermission) items.push({ id: 'delete', label: InovxLoader.t('facility:action.deleteAnnotation'), iconClass: 'delete-icon' });
        items.push({ id: 'reposition', label: InovxLoader.t('facility:action.repositionAnnotation'), iconClass: 'reposition-icon' });
        return items;
    },[]);

    const addToAssetSetLabel = React.useMemo(() => {
        return selectedAssetSet?.Name
            ? `${InovxLoader.t('facility:action.addToAssetSet')} '${selectedAssetSet.Name}'`
            : InovxLoader.t('facility:action.addToAssetSet');
    }, [selectedAssetSet?.Name]);

    const removeFromAssetSetLabel = React.useMemo(() => {
        return selectedAssetSet?.Name
            ? `${InovxLoader.t('facility:action.removeFromAssetSet')} '${selectedAssetSet.Name}'`
            : InovxLoader.t('facility:action.removeFromAssetSet');
    }, [selectedAssetSet?.Name]);

    const assetTagContextItems = useMemo(() => {
        const items = [];
        const facility = FacilityStore.getSelectedFacility();
        if (facility) {
            const selectItem = facility?.AssetTags?.find((ann) => ann.ID === assetTagContextMenu.tagId) || null;
            if (selectItem?.AnnotationType === AnnotationType.AssetTagWithBox) {
                items.push({ id: 'move', label: InovxLoader.t('facility:action.move'), iconClass: 'move-icon' },
                    { id: 'resize', label: InovxLoader.t('facility:action.resize'), iconClass: 'resize-icon' }
                );
            }
            items.push({ id: 'edit', label: InovxLoader.t('facility:action.editAssetTag'), iconClass: 'edit-icon' });
           
            // add delete only if user has manage tag permission
            if (hasManageTagsPermission) {
                items.push({
                    id: 'delete',
                    label: InovxLoader.t('facility:action.deleteAssetTag'),
                    iconClass: 'delete-icon'
                });
            }

            items.push({
                id: 'reposition',
                label: InovxLoader.t('facility:action.repositionAssetTag'),
                iconClass: 'reposition-icon'
            });

            if (selectedAssetSet?.Type === AssetSetType.List) {
                items.push({
                    id: 'addToAssetSet',
                    label: addToAssetSetLabel,
                    iconClass: 'add-selection-icon'
                });

                if ((selectedAssetSet.AssetTagIDs ?? []).includes(assetTagContextMenu.tagId)) {
                    items.push({
                        id: 'removeFromAssetSet',
                        label: removeFromAssetSetLabel,
                        iconClass: 'remove-icon'
                    });
                }
            }
        }

        return items;
    }, [
        hasModifyTagsPermission,
        hasManageTagsPermission,
        assetTagContextMenu.tagId,
        selectedAssetSet,
        addToAssetSetLabel,
        removeFromAssetSetLabel
    ]);


    const hideAssetTagContextMenu = () => {
        setAssetTagContextMenu(prev => prev.visible ? { visible: false, left: -1000, top: -1000, tagId: null } : prev);
    };

    const handleAssetTagContextMenuItemClick = async (itemId: string) => {
        const tagId = assetTagContextMenu.tagId;
        hideAssetTagContextMenu();
        if (!tagId) return;

        const tag = assetTags.find(t => t.ID === tagId);
        if (!tag) return;

        if (itemId === 'edit') {
            ViewerActions.editAssetTag(tag);
        } else if (itemId === 'delete') {
            ViewerActions.confirmDeleteAssetTag(tag);
        } else if (itemId === 'reposition') {
            ViewerActions.setPositionAnnotationDisplayMode();
        } else if (itemId === 'move') {
            ViewerActions.setVolumeChangeDisplayMode('tagmove');
        } else if (itemId === 'resize') {
            ViewerActions.setVolumeChangeDisplayMode('tagscale');
        } else if (itemId === 'addToAssetSet' || itemId === 'removeFromAssetSet') {
            const currentAssetSet = ViewerStore.getSelectedAssetSet();
            if (!currentAssetSet || currentAssetSet.Type !== AssetSetType.List) return;

            const assetTagIDs = new Set(currentAssetSet.AssetTagIDs ?? []);
            if (itemId === 'addToAssetSet') {
                assetTagIDs.add(tagId);
            } else {
                assetTagIDs.delete(tagId);
            }

            await AssetSetsActions.updateAssetSet({
                ...currentAssetSet,
                AssetTagIDs: Array.from(assetTagIDs)
            } as IAssetSet);
        }
    };
    
    const switchToCreateAnnotationMode = (tagType: AnnotationType) => {
        if (!isEditing) return;

        ViewerActions.setCreateAnnotationDisplayMode(tagType);
    }
    const deleteAnnotation = () => {
        const selectedKVTag = kvTagsInfo.tags?.find(tag => tag.ID == selectedAnnotation);
        ViewerActions.deleteAnnotation(selectedKVTag);
        ViewerActions.resetAnnotation();
    }

    const displayMiniMap = props.displayMiniMap && activeView;
    const visibleMapViews = useMemo(() => {
        const filteredViews = contentLayers.positions360
            ? views.filter(view => {
                if (view.ID === activeView?.ID) return false;
                return isScanPositionView(view)
                    ? (isBlackAndWhiteScanPosition(view)
                        ? contentLayers.blackAndWhiteScanPositions
                        : contentLayers.colorScanPositions)
                    : contentLayers.imagePositions;
            })
            : [];

        return activeView ? [activeView, ...filteredViews] : filteredViews;
    }, [views, activeView, contentLayers.positions360, contentLayers.colorScanPositions, contentLayers.blackAndWhiteScanPositions, contentLayers.imagePositions]);
    const displayText = (displayMode == DisplayMode.CreateAnnotation) ? InovxLoader.t('facility:message.pickTagLocation')
        : (displayMode == DisplayMode.PositionAnnotation) ? InovxLoader.t('facility:message.repositionTag')
            : (displayMode == DisplayMode.TagVolumeMove || displayMode == DisplayMode.TagVolumeScale) ? InovxLoader.t('facility:message.exitEditMode')
            : (measurementType == MeasurementType.Point) ? InovxLoader.t('facility:message.pickPointLocation')
                : (measurementType != null) ? InovxLoader.t('facility:message.pickDistanceLocation')
        : null;

    return (
        <>

            {!contextMenu && isAnnotationLinkDetailShow && <AnnotationLinkEditor
                onClose={() => setIsAnnotationLinkDetailShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            {!contextMenu && isAnnotationDetailShow && <AnnotationTextEditor
                onClose={() => setIsAnnotationDetailShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            {!contextMenu && isAnnotationTextShow && ViewerStore.getAnnotationEditorMode() && <AnnotationEditor
                onClose={() => setIsAnnotationTextShow(false)}
                left={lastAnnotationPosition.x}
                top={lastAnnotationPosition.y}
            />}
            {contextMenu && (
                <ContextMenu
                    left={lastClickPosition.x}
                    top={lastClickPosition.y}
                    items={annotationContextItems}
                    onItemClick={(itemId) => {
                        if (itemId === 'edit' || itemId === 'delete' || itemId === 'reposition') {
                            handleAnnotationMenuAction(itemId);
                        }
                    }}
                    containerId="annotation-context-menu"
                />
            )}
            {(assetTagContextMenu.visible && (hasModifyTagsPermission || hasManageTagsPermission)) && (
                <ContextMenu
                    left={assetTagContextMenu.left}
                    top={assetTagContextMenu.top}
                    items={assetTagContextItems}
                    onItemClick={handleAssetTagContextMenuItemClick}
                    onRequestClose={hideAssetTagContextMenu}
                    containerId="asset-tag-context-menu"
                />
            )}
            <div id="pano-viewer" className="pano-viewer-container minimap-container" ref={containerRef}>
                <ContentLayersButton assetSets={assetSets} />

                {(displayText) && (
                    <div className="overlay-text">{displayText}</div>
                )}
                {displayMiniMap && (
                    <FacilityMiniMap
                        siteMap={siteMap}
                        views={visibleMapViews}
                        title={facilityTitle}
                        targetId="#pano-viewer"
                        activeView={activeView}
                        highlightedView={highlightedView}
                        viewerDirectionDegrees={directionDegrees}
                        knowledgeViewOpened={ViewerStore.getSelectedKnowledgeView() != null}
                        pointCloudAvailable={ViewerStore.getSelectedViewImage()?.ScanFileData != null}
                        onViewMarkerClicked={navigateToView}
                        onViewMarkerHighlighted={(view, _, highlighted) => { onMapViewHighlighted(highlighted ? view : null); }}
                    />
                )}
            </div>
        </>
    );
});

export default ReactPanoViewer;
